import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { DataTable, SelectField, Tab, Tabs } from "components";
import {
  useGetAllCustomerUsers,
  useGetDattoDevices,
  useGetDattoMappedCustomers,
  useGetDattoMappedDevices,
  useMapDattoDevices,
} from "controller";
import { useSetAtom } from "jotai";
import { isDattoMappingListLoadingAtom } from "pages/modules/control-center/integrations/store/IntegrationStore";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { emptyStringHandler, getFullName } from "utilz/helpers";
import { useDebounce } from "utilz/helpers/hooks/useDebounceHook";

/**
 * Skeleton rows shown while a list is loading. DataTable renders
 * `[...Array(itemsPerPage)]` placeholders, so passing 0 here makes
 * `isDataLoading` render nothing at all — the table just looks empty.
 */
const LOADING_SKELETON_ROWS = 8;

export const DattoDeviceMapping = forwardRef(
  ({ onStepChange }: { onStepChange: (step: number) => void }, ref) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [selectedCustomer, setSelectedCustomer] = useState(0);

    // const [isSubmitting, setIsSubmitting] = useState(false);

    const {
      data: devicesData,
      isLoading: isDevicesLoading,
      fetchNextPage: fetchNextDevicesPage,
      isFetchingNextPage: isFetchingDevicesNextPage,
      hasNextPage: hasDevicesNextPage,
    } = useGetDattoDevices();

    // Datto returns ONE global device list that this screen groups by site, so a
    // site's tab is wrong until every page is in — a site whose devices sit on a
    // later page renders as "No data found". Pull the pages automatically rather
    // than making the user click through them blind.
    // Longer term this should fetch per selected site, the way NinjaOneStep2 does.
    useEffect(() => {
      if (hasDevicesNextPage && !isFetchingDevicesNextPage) {
        fetchNextDevicesPage();
      }
    }, [hasDevicesNextPage, isFetchingDevicesNextPage, fetchNextDevicesPage]);

    // True for the WHOLE walk, not just page 1, so the table shows its loader
    // instead of asserting "No data found" while later pages are still landing.
    const isDeviceListLoading =
      isDevicesLoading ||
      isFetchingDevicesNextPage ||
      Boolean(hasDevicesNextPage);

    // The step's Next/Finish button lives in the parent screen, so publish the
    // walk state for it to disable against. Cleared on unmount so leaving the
    // step never strands the button in a disabled state.
    const setIsMappingListLoading = useSetAtom(isDattoMappingListLoadingAtom);
    useEffect(() => {
      setIsMappingListLoading(isDeviceListLoading);
      return () => setIsMappingListLoading(false);
    }, [isDeviceListLoading, setIsMappingListLoading]);
    const {
      data: dattoMappedCustomers,
      isLoading: isDattoMappedCustomersLoading,
    } = useGetDattoMappedCustomers();

    // The mapping endpoint returns soft-deleted rows too, so an unmapped site
    // would otherwise keep its tab and its saved DeskDay customer. Derived once
    // on purpose: the tab list and `transformedCustomerData` are both indexed by
    // `selectedCustomer`, so filtering them separately would misalign the tabs.
    const activeMappedCustomers = useMemo(
      () =>
        (dattoMappedCustomers || []).filter(
          (customer: Record<string, any>) => customer.is_deleted === false,
        ),
      [dattoMappedCustomers],
    );
    const { data: dattoMappedDevices, isLoading: isDattoMappedDevicesLoading } =
      useGetDattoMappedDevices();
    const { mutateAsync: mapDevice } = useMapDattoDevices();

    const [transformedCustomerData, setTransformedCustomerData] = useState<
      any[]
    >([]);

    // The rows are built in the effects below, a render after the queries
    // settle. Keep the skeleton up until they exist.
    const isBuildingRows =
      Boolean(devicesData?.pages?.some((page) => page?.data?.length)) &&
      activeMappedCustomers.length > 0 &&
      Boolean(dattoMappedDevices) &&
      transformedCustomerData.length === 0;
    const [allDevices, setAllDevices] = useState<any[]>([]);

    useEffect(() => {
      if (devicesData?.pages) {
        const allDevices = devicesData.pages.flatMap(
          (page: any) => page?.data || [],
        );
        setAllDevices(allDevices);
      }
    }, [devicesData]);

    useEffect(() => {
      if (allDevices.length > 0 && dattoMappedCustomers && dattoMappedDevices) {
        createMappingArray();
      }
    }, [allDevices, activeMappedCustomers, dattoMappedDevices]);

    // Debounced server-side search — keeps users beyond the first page
    // reachable instead of filtering only the loaded page.
    const [userSearch, setUserSearch] = useDebounce("", 400);
    const { data: customerUsers, isFetching: isCustomerUsersFetching } =
      useGetAllCustomerUsers({
        perPageCount: 2000,
        sortColumn: "first_name",
        sortDirection: "asc",
        ...(transformedCustomerData.length > 0 && {
          customerId:
            transformedCustomerData[selectedCustomer]?.deskday_customer_id,
        }),
        filter: [
          { column: "customer_users.status", operator: "=", value: "true" },
        ],
        searchQuery: userSearch,
      });

    useEffect(() => {
      if (
        allDevices?.length > 0 &&
        dattoMappedCustomers &&
        dattoMappedDevices
      ) {
        createMappingArray();
      }
    }, [allDevices, activeMappedCustomers, dattoMappedDevices]);

    // const createMappingArray = async () => {
    //   const groupedByCustomerID = allDevices?.reduce(
    //     (acc: Record<string, any>, obj: Record<string, any>) => {
    //       if (!acc[obj.siteId]) {
    //         acc[obj.siteId] = [];
    //       }
    //       const deviceIndex = dattoMappedDevices?.findIndex(
    //         (device: Record<string, any>) =>
    //           String(device.device_id) === String(obj.id)
    //       );
    //       const data = {
    //         device_id: obj?.id,
    //         device_name: obj?.hostname,
    //         deskday_customer_user_id:
    //           deviceIndex !== -1
    //             ? dattoMappedDevices[deviceIndex].deskday_customer_user_id
    //             : "",
    //         deskday_customer_user_name:
    //           deviceIndex !== -1
    //             ? dattoMappedDevices[deviceIndex].deskday_customer_user_name
    //             : "",
    //         meta_data: obj,
    //       };
    //       acc[obj.siteId].push(data);
    //       return acc;
    //     },
    //     {}
    //   );

    //   const dataArray: any[] = [];
    //   dattoMappedCustomers?.map((customer: Record<string, any>) => {
    //     dataArray.push({
    //       mapping_id: customer.mapping_id,
    //       external_customer_id: customer.external_customer_id,
    //       // external_customer_name: customer.external_customer_name,
    //       deskday_customer_id: customer.deskday_customer_id,
    //       // deskday_customer_name: customer.deskday_customer_name,
    //       devices:
    //         groupedByCustomerID &&
    //         groupedByCustomerID.hasOwnProperty(customer.external_customer_id)
    //           ? groupedByCustomerID[customer.external_customer_id]
    //           : [],
    //     });
    //   });

    //   setTransformedCustomerData(dataArray);
    // };

    const createMappingArray = async () => {
      const groupedByCustomerID = allDevices?.reduce(
        (acc: Record<string, any>, obj: Record<string, any>) => {
          // Explicitly convert siteId to string for safe grouping
          const siteId = String(obj.siteId);
          if (!acc[siteId]) {
            acc[siteId] = [];
          }
          const deviceIndex = dattoMappedDevices?.findIndex(
            (device: Record<string, any>) =>
              String(device.device_id) === String(obj.id) &&
              device.is_deleted === false,
          );
          const data = {
            device_id: obj?.id,
            device_name: obj?.hostname,
            deskday_customer_user_id:
              deviceIndex !== -1
                ? dattoMappedDevices[deviceIndex].deskday_customer_user_id
                : "",
            deskday_customer_user_name:
              deviceIndex !== -1
                ? dattoMappedDevices[deviceIndex].deskday_customer_user_name
                : "",
            meta_data: obj,
          };
          acc[siteId].push(data);
          return acc;
        },
        {},
      );
      const dataArray: any[] = [];
      activeMappedCustomers?.map((customer: Record<string, any>) => {
        // Convert external_customer_id to string for lookup
        const customerId = String(customer.external_customer_id);
        dataArray.push({
          mapping_id: customer.mapping_id,
          external_customer_id: customer.external_customer_id,
          deskday_customer_id: customer.deskday_customer_id,
          devices:
            groupedByCustomerID &&
            groupedByCustomerID.hasOwnProperty(customer.external_customer_id)
              ? groupedByCustomerID[customer.external_customer_id]
              : [],
        });
      });
      setTransformedCustomerData(dataArray);
    };

    const handleUserChange = ({
      id,
      cellData,
      rowIndex,
    }: {
      id: string;
      cellData: Record<string, any>;
      rowIndex: number;
    }) => {
      if (!id) {
        setTransformedCustomerData((prev) => {
          const newPrev = [...prev];
          newPrev[selectedCustomer].devices[rowIndex].deskday_customer_user_id =
            "";
          newPrev[selectedCustomer].devices[
            rowIndex
          ].deskday_customer_user_name = "";
          return newPrev;
        });
        return;
      }

      const index = customerUsers?.result.findIndex(
        (user: Record<string, any>) => user.mapping_id === id,
      );

      if (index !== -1) {
        setTransformedCustomerData((prev) => {
          const fullname = getFullName(
            customerUsers?.result[index].first_name,
            customerUsers?.result[index].middle_name,
            customerUsers?.result[index].last_name,
          );
          const newPrev = [...prev];
          newPrev[selectedCustomer].devices[rowIndex].deskday_customer_user_id =
            customerUsers?.result[index].mapping_id;
          newPrev[selectedCustomer].devices[
            rowIndex
          ].deskday_customer_user_name = fullname;
          return newPrev;
        });
      }
    };

    const handleTransformForSubmit = () => {
      const submitArray: Array<any> = [];
      transformedCustomerData?.map((customer: Record<string, any>) => {
        const devices = customer.devices.filter(
          (device: Record<string, any>) =>
            device.deskday_customer_user_id !== "" &&
            device.deskday_customer_user_name !== "",
        );
        if (devices.length > 0) {
          submitArray.push({
            ...customer,
            devices,
          });
        }
      });

      return submitArray;
    };

    const cellBuilders = {
      user_name: (cellData: any, index?: number) => {
        return (
          <SelectField
            key={cellData.device_name}
            sx={{ width: "180px" }}
            name="customer"
            hideHelperBox
            options={customerUsers?.result || []}
            isSearchable
            isLoading={isCustomerUsersFetching}
            onSearchValueChange={setUserSearch}
            // Search is served by the backend (`searchQuery`), so `onFilterValue`
            // returns true to stop the client from re-filtering the results.
            onFilterValue={() => true}
            value={cellData.deskday_customer_user_id}
            size="small"
            isResponsive={false}
            labelMappingKey="first_name"
            valueMappingKey="mapping_id"
            searchFieldProps={{
              placeholder: "Search user",
            }}
            menuItemBuilder={({ item, index }) => (
              <Box key={index}>
                {emptyStringHandler(item.first_name)}{" "}
                {emptyStringHandler(item.last_name)}
              </Box>
            )}
            onValueChange={(value) =>
              handleUserChange({
                id: value,
                cellData,
                rowIndex: index || 0,
              })
            }
            isClearable
            // The options list shrinks while a search is active, so fall back to
            // the name held in row state to keep every row's label stable.
            renderValue={(_selected, selectedOption) => {
              return selectedOption ? (
                <>
                  {emptyStringHandler(selectedOption?.first_name)}{" "}
                  {emptyStringHandler(selectedOption?.last_name)}
                </>
              ) : (
                emptyStringHandler(cellData.deskday_customer_user_name)
              );
            }}
          />
        );
      },
    };

    const onSubmit = async () => {
      // The backend full-replaces device mappings from this payload, and the
      // payload is built only from devices already loaded. Saving mid-walk would
      // drop every device still sitting on an unfetched page, so hold the step
      // until the list has settled rather than writing a partial mapping.
      // Backstop only — the Next button is already disabled while this is true.
      // The "Save your changes?" modal reaches onSubmit by another path, so the
      // check stays here too.
      if (isDeviceListLoading) {
        toast.error("Devices are still loading. Please try again in a moment.");
        return false;
      }
      const data = handleTransformForSubmit();
      if (data.length > 0) {
        try {
          await mapDevice({ device_mapping: data });
          toast.success("Device mapping saved");
          return true;
        } catch (error) {
          toast.error("Failed to map devices.");
          return false;
        }
      } else {
        return true;
      }
    };

    useImperativeHandle(ref, () => ({
      onSubmit,
    }));

    return (
      <Stack
        sx={{
          py: "24px",
          height: "calc(100% - 34px)",
          overflowY: "scroll",
        }}
        divider={
          <Divider
            flexItem
            sx={{
              marginTop: "24px",
            }}
          />
        }
      >
        <Stack sx={{ px: "32px", gap: "16px" }}>
          <Typography variant="h6">Device mapping</Typography>
          <Typography variant="body-medium" color={theme.palette.lightBg.low}>
            Link Datto devices to DeskDay users to auto-map alert tickets.
            Alerts from unmapped devices will create tickets with the system
            user.
          </Typography>
        </Stack>

        <Stack
          direction={"row"}
          flex={1}
          sx={{
            width: "100%",
            overflow: "clip",
          }}
          divider={<Divider orientation="vertical" flexItem />}
        >
          <Box
            sx={{
              display: "block",
              overflowY: "scroll",
              height: "100%",
              width: "350px",
            }}
          >
            <Stack p="12px" height="40px" textAlign={"center"}>
              <Typography variant="caption" fontSize={"14px"}>
                Mapped DeskDay customers
              </Typography>
            </Stack>
            <Tabs
              orientation="vertical"
              value={selectedCustomer}
              onChange={(_, index) => {
                setSelectedCustomer(index);
              }}
            >
              {activeMappedCustomers?.map(
                (customer: Record<string, any>, index: number) => (
                  <Tab
                    key={customer.id}
                    label={customer.external_customer_name}
                  />
                ),
              )}
            </Tabs>
          </Box>
          <Box
            sx={{
              display: "block",
              overflowY: "scroll",
              height: "100%",
              flex: "1",
              padding: "32px 32px 0px 32px",
            }}
          >
            <DataTable
              isDataLoading={
                isDeviceListLoading ||
                isDattoMappedCustomersLoading ||
                isDattoMappedDevicesLoading ||
                isBuildingRows
              }
              headers={TableHeaders}
              data={transformedCustomerData[selectedCustomer]?.devices || []}
              uniqueKey="device_id"
              cellBuilders={cellBuilders}
              selectedItems={[]}
              currentPageNumber={0}
              itemsPerPage={LOADING_SKELETON_ROWS}
              multiselect={false}
              containerStyles={{ height: "100%" }}
              headerCellStyles={{
                textTransform: "none",
              }}
            />
          </Box>
        </Stack>
      </Stack>
    );
  },
);

const TableHeaders = [
  {
    name: "Datto device",
    sortable: false,
    key: "device_name",
  },
  {
    name: "DeskDay user",
    sortable: true,
    key: "user_name",
  },
];
